import { useMemo, useState } from "react";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "../../../AppSidebar";
import "../../../V2SideDrawer.css";
import "../../common/styles/FeatureTable.css";
import "../styles/RetroViewV2.css";
import { useRetroData } from "../hooks/useRetroData";
import { useRetroViewState } from "../hooks/useRetroViewState";
import { findMemberDetail } from "../services/retroDerivations";

import RetroHeader from "../components/RetroHeader";
import RetroTopTabs from "../components/RetroTopTabs";
import ZoneProgressCard from "../components/dashboard/ZoneProgressCard";
import RadarComparisonCard from "../components/dashboard/RadarComparisonCard";
import TrendCard from "../components/dashboard/TrendCard";
import SignalChipsCard from "../components/dashboard/SignalChipsCard";
import PlaybookCard from "../components/dashboard/PlaybookCard";
import GoalsCard from "../components/dashboard/GoalsCard";
import CompetenciesCard from "../components/dashboard/CompetenciesCard";
import ReflectionCard from "../components/dashboard/ReflectionCard";
import TeamRollupCards from "../components/team/TeamRollupCards";
import TeamMembersTable from "../components/team/TeamMembersTable";
import TeamMemberReviewDrawer from "../components/team/TeamMemberReviewDrawer";
import LearningsList from "../components/learn/LearningsList";
import LearningsSidebar from "../components/learn/LearningsSidebar";
import SubmitReflectionModal from "../components/modals/SubmitReflectionModal";

export default function RetroViewV2Page({ user }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    localStorage.getItem("sb-collapsed") === "true"
  );
  const [toast, setToast] = useState(null);

  const { loading, error, viewModel, reload, tabs } = useRetroData();
  const state = useRetroViewState(viewModel);

  const selectedMember = useMemo(
    () => viewModel.team.members.find((m) => m.id === state.selectedMemberId) || null,
    [viewModel, state.selectedMemberId]
  );
  const selectedMemberDetail = useMemo(
    () => findMemberDetail(viewModel.team, state.selectedMemberId),
    [viewModel, state.selectedMemberId]
  );

  const submittedCount = Object.values(state.submittedWeeks).filter(Boolean).length;
  const totalCount = viewModel.submitReflection.weeks.length;

  const submitAndToast = () => {
    state.submitCurrentWeek();
    setToast("Reflection submitted");
    setTimeout(() => setToast(null), 2200);
  };

  return (
    <div
      className="rtv2-root"
      style={{ gridTemplateColumns: sidebarCollapsed ? `${SB_W_COLLAPSED} 1fr` : `${SB_W} 1fr` }}
    >
      <AppSidebar
        user={user}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed((c) => {
          const next = !c;
          localStorage.setItem("sb-collapsed", String(next));
          return next;
        })}
      />

      <div className="rtv2-main">
        <RetroHeader
          cycle={viewModel.cycle}
          onOpenSubmit={() => state.setSubmitOpen(true)}
          submittedCount={submittedCount}
          totalCount={totalCount}
        />

        <RetroTopTabs
          tabs={tabs}
          activeTab={state.activeTab}
          onChange={state.setActiveTab}
          learningsCount={viewModel.learnings.lessons.length}
        />

        <div className="rtv2-body">
          {loading ? <div className="rtv2-state">Loading retro data…</div> : null}

          {!loading && error ? (
            <div className="rtv2-state">
              Unable to load retro data. {error}
              <div style={{ marginTop: 10 }}>
                <button type="button" className="v2-btn-secondary" onClick={reload}>Retry</button>
              </div>
            </div>
          ) : null}

          {!loading && !error && state.activeTab === "dash" ? (
            <div className="rtv2-dash">
              <ZoneProgressCard zone={viewModel.dashboard.zone} />

              <RadarComparisonCard
                radar={viewModel.dashboard.radar}
                period={state.radarPeriod}
                setPeriod={state.setRadarPeriod}
                seriesAId={state.radarSeriesA}
                setSeriesAId={state.setRadarSeriesA}
                seriesBId={state.radarSeriesB}
                setSeriesBId={state.setRadarSeriesB}
              />

              <TrendCard trend={viewModel.dashboard.trend} />

              <SignalChipsCard
                title="Strength highlights"
                subtitle="Surfaced from peer + self signals"
                items={viewModel.dashboard.strengths}
                tone="good"
              />

              <SignalChipsCard
                title="Growth areas"
                subtitle="Soft signals · not blockers"
                items={viewModel.dashboard.growthAreas}
                tone="focus"
              />

              <PlaybookCard items={viewModel.dashboard.playbook} />

              <div className="rtv2-grid-2">
                <GoalsCard goals={viewModel.dashboard.goals} />
                <CompetenciesCard competencies={viewModel.dashboard.competencies} />
              </div>

              <ReflectionCard reflections={viewModel.dashboard.reflections} />
            </div>
          ) : null}

          {!loading && !error && state.activeTab === "team" ? (
            <div className="rtv2-team">
              <TeamRollupCards items={viewModel.team.rollup} />
              <TeamMembersTable members={viewModel.team.members} onOpenMember={state.setSelectedMemberId} />
            </div>
          ) : null}

          {!loading && !error && state.activeTab === "learn" ? (
            <div className="rtv2-learn-layout">
              <LearningsList lessons={viewModel.learnings.lessons} />
              <LearningsSidebar
                themes={viewModel.learnings.themes}
                commits={viewModel.learnings.commits}
                quote={viewModel.learnings.quote}
              />
            </div>
          ) : null}
        </div>
      </div>

      <TeamMemberReviewDrawer
        open={!!selectedMember}
        member={selectedMember}
        detail={selectedMemberDetail}
        cadence={state.memberGoalCadence}
        onCadenceChange={state.setMemberGoalCadence}
        onClose={() => state.setSelectedMemberId(null)}
      />

      <SubmitReflectionModal
        open={state.submitOpen}
        onClose={() => state.setSubmitOpen(false)}
        submitConfig={viewModel.submitReflection}
        selectedWeekId={state.selectedWeekId}
        setSelectedWeekId={state.setSelectedWeekId}
        submittedWeeks={state.submittedWeeks}
        promptDrafts={state.promptDrafts}
        setPromptValue={state.setPromptValue}
        visibility={state.visibility}
        setVisibility={state.setVisibility}
        onConfirm={submitAndToast}
      />

      {toast ? <div className="rtv2-toast">{toast}</div> : null}
    </div>
  );
}
